import { Amplify } from 'aws-amplify';

// Decide origins
const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const APP_ORIGIN = isLocalhost
  ? `${window.location.protocol}//localhost:3003`
  : window.location.origin;

// IMPORTANT: use a dedicated callback route
const CALLBACK_URL = `${APP_ORIGIN}/auth/callback`;
const LOGOUT_URL   = `${APP_ORIGIN}/`;

// If you use a custom domain, env should be like "auth.codiii.com"
// If you use the Cognito default domain, env should be like "ca-central-1xxxx.auth.ca-central-1.amazoncognito.com"
const OAUTH_DOMAIN_HOST = process.env.REACT_APP_AWS_OAUTH_DOMAIN;       // no protocol
const OAUTH_DOMAIN_FULL = `https://${OAUTH_DOMAIN_HOST}`;                // with protocol

// Amplify only for Storage/API (Auth block is ok to keep, but we won't *use* it to sign-in)
const awsConfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
      signUpVerificationMethod: 'code',
      loginWith: {
        oauth: {
          // domain must be host only (no https://) for Amplify
          domain: OAUTH_DOMAIN_HOST,
          scopes: ['openid', 'email', 'profile', 'offline_access'],
          redirectSignIn: [CALLBACK_URL],
          redirectSignOut: [LOGOUT_URL],
          responseType: 'code'
        }
      }
    }
  },
  Storage: {
    S3: {
      bucket: process.env.REACT_APP_STORAGE_BUCKET_NAME,
      region: process.env.REACT_APP_STORAGE_REGION
    }
  },
  API: {
    GraphQL: {
      endpoint: process.env.REACT_APP_APPSYNC_API_URL,
      region: process.env.REACT_APP_AWS_REGION || 'ca-central-1',
      defaultAuthMode: 'userPool',
    }
  }
};

Amplify.configure(awsConfig);

// OIDC client config for react-oidc-context (the one actually doing login)
const oidcConfig = {
  authority: OAUTH_DOMAIN_FULL,
  client_id: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
  // IMPORTANT: must be the callback route your app handles
  redirect_uri: CALLBACK_URL,
  post_logout_redirect_uri: LOGOUT_URL,
  response_type: 'code',
  scope: 'openid email profile offline_access',
  loadUserInfo: true,

  // Cognito well-known endpoints (with protocol)
  metadata: {
    issuer: OAUTH_DOMAIN_FULL,
    authorization_endpoint: `${OAUTH_DOMAIN_FULL}/oauth2/authorize`,
    token_endpoint:         `${OAUTH_DOMAIN_FULL}/oauth2/token`,
    userinfo_endpoint:      `${OAUTH_DOMAIN_FULL}/oauth2/userInfo`,
    end_session_endpoint:   `${OAUTH_DOMAIN_FULL}/logout`,
    revocation_endpoint:    `${OAUTH_DOMAIN_FULL}/oauth2/revoke`
  },

  // DO NOT put client_secret in the browser — use a public client
  // extraTokenParams: { client_secret: '❌ remove for SPA' },

  onSigninCallback: (user) => {
    // react-oidc-context stores tokens; this is optional
    if (user) {
      if (user.access_token)  localStorage.setItem('access_token', user.access_token);
      if (user.id_token)      localStorage.setItem('id_token', user.id_token);
      if (user.refresh_token) localStorage.setItem('refresh_token', user.refresh_token);
      window.dispatchEvent(new Event('auth-tokens-changed'));
    }
    // clean query
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  onSigninError: (error) => {
    console.error('Sign-in error:', error);
  },

  // Silent renew is often flaky with Cognito due to third-party cookie/iframe limits.
  // You can leave it on; just know it might not always work in Safari.
  automaticSilentRenew: true,
  monitorSession: true,
  filterProtocolClaims: true,
  clockSkew: 300
};

// Hosted UI sign-out helper
export const signOutRedirect = () => {
  const clientId = process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID;
  const url =
    `${OAUTH_DOMAIN_FULL}/logout?client_id=${encodeURIComponent(clientId)}` +
    `&logout_uri=${encodeURIComponent(LOGOUT_URL)}`;

  try {
    localStorage.clear();
    sessionStorage.clear();
  } finally {
    window.location.href = url;
  }
};

export { awsConfig };
export default oidcConfig;
